/* eslint-disable @typescript-eslint/no-unused-vars */
import { LoaderCircleIcon, SearchIcon, UsersRoundIcon } from 'lucide-react';
import { useState } from 'react';
import { AsyncTypeahead, Menu, MenuItem } from 'react-bootstrap-typeahead';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { MEDIA_URL } from '@/config';
import { api } from '@/lib/api-client';
import { Team } from '@/types';

interface SearchData {
  teams: Team[];
}

interface TeamSearchProps {
  onTeamSelect: (team: Team) => void;
  isDrawer: boolean;
}

export const TeamSearch: React.FC<TeamSearchProps> = ({
  onTeamSelect,
  isDrawer,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<SearchData>({
    teams: [],
  });

  const handleSearch = (query: string) => {
    setIsLoading(true);

    api.get(`/search/main?query=${query}`).then((response: any) => {
      setOptions({ teams: response.teams });
      setIsLoading(false);
    });
  };

  const filterBy = () => true;
  const labelKey: any = (option: Team) => option.team_name || '';

  const handleChange = (selected: any[]) => {
    if (selected.length > 0) {
      onTeamSelect(selected[0]);
    }
  };

  return (
    <AsyncTypeahead
      id="teamSearch"
      filterBy={filterBy}
      renderInput={({ inputRef, referenceElementRef, ...inputProps }) => (
        <div className="relative">
          <Input
            {...inputProps}
            className="peer ps-9"
            placeholder="Search..."
            type="search"
            ref={(node) => {
              inputRef(node);
              referenceElementRef(node);
            }}
          />
          <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 text-muted-foreground/80 peer-disabled:opacity-50">
            {isLoading ? (
              <LoaderCircleIcon
                className="animate-spin"
                size={16}
                role="status"
                aria-label="Loading..."
              />
            ) : (
              <SearchIcon size={16} aria-hidden="true" />
            )}
          </div>
        </div>
      )}
      className="w-full"
      isLoading={isLoading}
      labelKey={labelKey}
      minLength={2}
      onChange={handleChange}
      onSearch={handleSearch}
      options={options.teams}
      dropup={isDrawer}
      promptText={
        <div className="py-2 text-center text-sm">Type to search</div>
      }
      emptyLabel={
        <div className="py-2 text-center text-sm">
          Nothing found, try searching again.
        </div>
      }
      searchText={<div className="py-2 text-center text-sm">Searching...</div>}
      renderMenu={(
        results,
        {
          newSelectionPrefix,
          paginationText,
          renderMenuItemChildren,
          ...menuProps
        },
        state,
      ) => {
        return (
          <Menu
            className={`z-50 ${
              isDrawer ? '-mt-2' : 'mt-2'
            } !flex w-full flex-col rounded-lg border border-border bg-popover p-2 shadow`}
            {...menuProps}
          >
            {results.map((result: any, index) => {
              const label = result.team_name;
              const image = `${MEDIA_URL}/images/uploads/${result.team_photo}`;

              return (
                <MenuItem
                  key={index}
                  className={`flex items-center gap-x-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted/60 focus:ring-2 focus:ring-primary ${
                    index === state.activeIndex ? 'bg-muted/60' : ''
                  }`}
                  option={result}
                  position={index}
                >
                  <Avatar className="size-8">
                    <AvatarImage src={image} alt={label} />
                    <AvatarFallback className="rounded-lg">
                      <UsersRoundIcon
                        size={16}
                        className="opacity-60"
                        aria-hidden="true"
                      />
                    </AvatarFallback>
                  </Avatar>
                  <div className="mx-2 truncate" title={label}>
                    {label}
                  </div>
                </MenuItem>
              );
            })}
          </Menu>
        );
      }}
    />
  );
};
